import React from "react"
import {
	Assignee,
	AssignmentActionRequestType,
	AssignmentStage,
	AssignmentStatus,
	DrawdownAuthorizationReviewApproveRequest,
	DrawdownAuthorizationReviewAssignmentItemPayload,
	DrawdownAuthorizationReviewRejectReason,
	DrawdownAuthorizationReviewRejectRequest,
	update,
} from "../../resources/assignments"
import {WirePayment} from "../../resources/payment"
import {Customer} from "../../resources/customer"
import {BusinessApplication} from "../../resources/application"
import {useResetScrollTop} from "../../hooks/useResetScrollTop"
import {AssignmentPageItem} from "../AssignmentPageItem/AssignmentPageItem"
import {AssignmentPageItemApproveRejectFooter} from "../AssignmentPageItemApproveRejectFooter/AssignmentPageItemApproveRejectFooter"
import {DetailsList} from "../DetailsList/DetailsList"
import {Claims} from "../../services/auth"
import {useModal} from "react-modal-hook"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {
	AssignmentApproveRejectModal,
	AssignmentStatusUpdateReasonsMapping,
} from "../AssignmentApproveRejectModal/AssignmentApproveRejectModal"
import {AssignmentPageCompletedItemHeader} from "../AssignmentPageCompletedItemHeader/AssignmentPageCompletedItemHeader"
import {Account} from "../../resources/account"
import {KeyValueCardView} from "../RenderKeyValueCard/KeyValueCardView"
import {GridContainer} from "../../containers/GridContainer/GridContainer"
import moment from "moment"
import {ValueAndUnits} from "../ValueAndUnits/ValueAndUnits"
import {addressText} from "../../utilities/helpers"
import {PdfDocumentViewer} from "../PdfDocumentViewer/PdfDocumentViewer"

interface AssignmentPageItemDrawdownAuthorizationReviewProps {
	id: string
	assignees: Assignee[]
	status: AssignmentStatus
	stage: AssignmentStage
	title: string
	description?: string | JSX.Element
	createdAt: Date
	statusUpdatedAt?: Date
	isOwnersItem?: boolean
	wire?: WirePayment
	account?: Account
	customer?: Customer
	wireDrawdownCreationDate?: Date
	paymentAuthorizationRuleDocumentUrl: string
	businessApplication?: BusinessApplication
	payload: DrawdownAuthorizationReviewAssignmentItemPayload
	accessToken: string
	refresh: () => void
	disabled?: boolean
	claims?: Claims
}

export enum DrawdownAuthorizationReviewStatusUpdateReasonKeys {
	SuspectedFraud = "SuspectedFraud",
	ClientRequest = "ClientRequest",
	InsufficientFunds = "InsufficientFunds",
	InvalidName = "InvalidName",
	InvalidRoutingNumber = "InvalidRoutingNumber",
	Other = "Other",
}

const drawdownAuthorizationReviewAssignmentStatusUpdateReasons: AssignmentStatusUpdateReasonsMapping = {
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.SuspectedFraud]: {
		reason: "SuspectedFraud",
		displayName: "Suspected Fraud",
	},
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.ClientRequest]: {
		reason: "ClientRequest",
		displayName: "Client Request",
	},
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.InsufficientFunds]: {
		reason: "InsufficientFunds",
		displayName: "Insufficient Funds",
	},
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.InvalidName]: {
		reason: "InvalidName",
		displayName: "Invalid Name",
	},
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.InvalidRoutingNumber]: {
		reason: "InvalidRoutingNumber",
		displayName: "Invalid Routing Number",
	},
	[DrawdownAuthorizationReviewStatusUpdateReasonKeys.Other]: {reason: "Other", displayName: "Other"},
}

export function AssignmentPageItemDrawdownAuthorizationReviewView({
	id,
	assignees,
	status,
	stage,
	title,
	description,
	createdAt,
	statusUpdatedAt,
	isOwnersItem,
	wire,
	payload: {reason, reasonText},
	wireDrawdownCreationDate,
	accessToken,
	refresh,
	disabled,
	paymentAuthorizationRuleDocumentUrl,
}: AssignmentPageItemDrawdownAuthorizationReviewProps) {
	const wireDetailsContentRef = React.useRef<HTMLDivElement>(null)
	useResetScrollTop(wireDetailsContentRef, [id])

	const [showApprove, hideApprove] = useModal(() => {
		const successText = "Approved successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = (args: {reasonText: string} | null) => {
			const data: DrawdownAuthorizationReviewApproveRequest = {
				type: AssignmentActionRequestType.drawdownAuthorizationReviewApproveRequest,
				attributes: {
					reasonText: args?.reasonText,
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Approve Item"
				description={
					<p>
						Are you sure you want to approve{" "}
						<span className="has-text-weight-bold">{`Drawdown Authorization Review #${id}?`}</span>
					</p>
				}
				state={state}
				onSubmit={onSubmit}
				withReasonText
				isOptionalReasonText
				reasonTextPlaceholder="Please provide a comment if there were initial concerns cleared by additional outreach or investigation."
				primaryButtonText="Approve"
				primaryButtonClassname="is-success"
				closeModal={hideApprove}
				onSuccess={refresh}
				successText={successText}
			/>
		)
	}, [id])

	const [showReject, hideReject] = useModal(() => {
		const successText = "Rejected successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reason, reasonText}: {reason: DrawdownAuthorizationReviewRejectReason; reasonText?: string}) => {
			const data: DrawdownAuthorizationReviewRejectRequest = {
				type: AssignmentActionRequestType.drawdownAuthorizationReviewRejectRequest,
				attributes: {
					reason,
					...(reason === DrawdownAuthorizationReviewRejectReason.Other ? {reasonText} : {}),
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Reject Item"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				reasonTextPlaceholder="Why are you rejecting this based on drawdown authorization review?"
				reasons={drawdownAuthorizationReviewAssignmentStatusUpdateReasons}
				reasonsToEnableReasonText={[DrawdownAuthorizationReviewStatusUpdateReasonKeys.Other]}
				primaryButtonText="Reject"
				primaryButtonClassname="is-danger"
				closeModal={hideReject}
				onSuccess={refresh}
				successText={successText}
				successClassname="has-text-black"
			/>
		)
	}, [id])

	const detailMissing = "N/A"
	const dateFormat = "MM/DD/YYYY"

	const drawdownDetails = {
		"Wire Creation Date": wire?.attributes.createdAt
			? moment(wire.attributes.createdAt).format(dateFormat)
			: detailMissing,
		"Request Creation Date": wireDrawdownCreationDate
			? moment(wireDrawdownCreationDate).format(dateFormat)
			: detailMissing,
		"Drawdown Amount": wire?.attributes.amount ? (
			<ValueAndUnits
				value={wire.attributes.amount / 100}
				unitsPosition="Prefix"
				size="medium"
				units="$"
				valueClassName={"is-size-6"}
				unitsClassName={"is-size-6"}
				formatMethod="with_commas"
			/>
		) : (
			detailMissing
		),
		"Beneficiary Name": wire?.attributes.counterparty.name || detailMissing,
		"Beneficiary Address": wire?.attributes.counterparty.address
			? addressText(wire?.attributes.counterparty.address)
			: detailMissing,
		"Beneficiary Bank Routing Number": wire?.attributes.counterparty.routingNumber ?? detailMissing,
		"Beneficiary Account Number": wire?.attributes.counterparty.accountNumber ?? detailMissing,
	}

	return (
		<AssignmentPageItem
			assignees={assignees}
			status={status}
			stage={stage}
			title={title}
			description={description}
			createdAt={createdAt}
			statusUpdatedAt={statusUpdatedAt}
			isOwnersItem={isOwnersItem}
			disabled={disabled}
			pageItemFooter={
				!disabled ? (
					<AssignmentPageItemApproveRejectFooter
						approveLabel="Approve"
						rejectLabel="Reject"
						onApprove={showApprove}
						onReject={showReject}
					/>
				) : undefined
			}
			pageCompletedItemHeader={
				<AssignmentPageCompletedItemHeader
					assignees={assignees}
					status={status}
					reason={reason}
					reasonText={reasonText}
				/>
			}
		>
			<GridContainer rows={1} columns={2} gap="8px" className="has-full-height assignment-page-item-default-inner-grid">
				<DetailsList
					title="Drawdown Details"
					icon="check-write--business-products-1"
					className="scrollable-container"
					contentRef={wireDetailsContentRef}
					disabled={disabled}
				>
					{wire && (
						<div className="drawdown-authorization-review-details">
							<KeyValueCardView details={drawdownDetails} singleColumn className="is-paddingless" />
						</div>
					)}
				</DetailsList>
				<PdfDocumentViewer pdfUrl={paymentAuthorizationRuleDocumentUrl} />
			</GridContainer>
		</AssignmentPageItem>
	)
}
